import EmailSent from '../EmailSent';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Heading from '@atlaskit/heading';
import React, { useMemo } from 'react';
import TallButton from '../TallButton';
import Whitebox from '../WhiteboxPage';
import messages from './SendOtpEmailInterstitial.messages';
import styled from '@emotion/styled';
import { FormattedMessage, useIntl } from 'react-intl';
import PageTitle from '../PageTitle';
import { colors } from '@atlaskit/theme';

export type SendOtpEmailInterstitialErrorCode =
  | 'rate_limited'
  | 'link_rate_limited'
  | 'internal_server_error'
  | 'unexpected_error';

export type SendOtpEmailInterstitialProps = {
  pageId: string;
  email: string;
  isLoading: boolean;
  errorCode?: SendOtpEmailInterstitialErrorCode;
  onSendVerificationEmail: () => void;
};

const SendOtpEmailInterstitialError = styled.div`
  margin-top: 8px;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  font-size: 12px !important;
  color: ${colors.R500};
`;

export const SendOtpEmailInterstitial = ({
  pageId,
  email,
  isLoading,
  errorCode,
  onSendVerificationEmail,
}: SendOtpEmailInterstitialProps) => {
  const { formatMessage } = useIntl();

  const errorMessageDescriptor = useMemo(() => {
    switch (errorCode) {
      case undefined:
        return undefined;
      case 'link_rate_limited':
        return messages.errorLinkRateLimited;
      case 'rate_limited':
        return messages.errorRateLimited;
      case 'internal_server_error':
        return messages.errorInternalServerError;
      case 'unexpected_error':
        return messages.errorUnexpected;
      default:
        return messages.errorUnexpected;
    }
  }, [errorCode]);

  return (
    <Whitebox
      pageId={pageId}
      header={
        <Heading as="h1" size="medium">
          <FormattedMessage {...messages.headTitle} />
        </Heading>
      }>
      <PageTitle title={formatMessage(messages.headTitle)} />
      <EmailSent email={email} message={formatMessage(messages.body)} withEmailSentImage={false} />
      <TallButton
        id="send-verification-email-submit"
        appearance={'primary'}
        type="submit"
        isFullWidth
        style={{ marginTop: '30px' }}
        onClick={onSendVerificationEmail}
        isLoading={isLoading}>
        {formatMessage(messages.sendButton)}
      </TallButton>
      {!!errorMessageDescriptor && (
        <SendOtpEmailInterstitialError role="alert">
          <ErrorIcon
            label={formatMessage({
              id: 'send.verification.email.error.icon.label',
              defaultMessage: 'Error',
              description: 'Accessible label for the error icon displayed next to error messages',
            })}
            size={'small'}
          />
          {formatMessage(errorMessageDescriptor)}
        </SendOtpEmailInterstitialError>
      )}
    </Whitebox>
  );
};

export default SendOtpEmailInterstitial;
